import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { getApplicableDayAdjustments, getApplicableSeasonalAdjustments, applyPriceAdjustments, getProgressiveAdjustment } from "utils/calculateBookingTotal"
import { isDynamicMultiDay, countDays } from "utils/multiDayPricing"
import { getTicketRates } from "utils/widgetBookingPricing"
import { getBookableTicketTypes } from "utils/experience"
import { EXPERIENCE_TYPES } from "constants/experiences"

const ISO = "YYYY-MM-DD"

// Which per-person guest types can appear in the discount breakdown, and the trip_prices
// field each reads its base rate + "is this guest type sold at all" flag from. Adults have
// no allowed flag — every pricing option sells to adults by definition.
const GUEST_TYPES = [
  { key: "adults", rateField: "price_per_person", allowedField: null },
  { key: "children", rateField: "price_per_child", allowedField: "children_allowed" },
  { key: "infants", rateField: "price_per_infant", allowedField: "infants_allowed" },
  { key: "passengers", rateField: "price_per_passenger", allowedField: "passengers_allowed" }
]

/**
 * Per-date "is this day actually cheaper than usual" info for a calendar cell, shared by
 * every place that shows a trip's calendar (the experience-creation preview, and the guide
 * manual/embedded/marketplace booking pickers). A day only counts as discounted when its
 * adjustments — day-based, seasonal, and (booking pickers only, see options.rangeStart below)
 * progressive duration pricing — net out to a LOWER price than the base rate. They can stack
 * in opposite directions (e.g. +5% / -2%), and a net increase or no change is shown as a plain
 * day, not a partial discount.
 *
 * @param {Object} trip - carries day_adjustments/seasonal_adjustments/trip_prices/pricing_type/max_guests
 * @param {Array<{start,end}>} [seasonRanges] - trip.available_date_ranges reshaped for "default_schedule"
 *   seasonal adjustments. Optional — callers with no reason to reshape it themselves (i.e. anything
 *   but the create-experience wizard, which prefers live in-progress form values) can omit it and
 *   this derives the same shape straight from trip.available_date_ranges.
 * @param {Object} [options]
 * @param {Date|dayjs.Dayjs} [options.rangeStart] - the stay's currently-picked start date, for a
 *   dynamic multi-day trip's booking pickers only (the experience-creation preview calendar has
 *   no picked stay and omits this). Day-based and seasonal adjustments always evaluate on EACH
 *   date's own calendar day (a deliberate product choice — NOTE this is not what the backend
 *   actually charges, which decides day/seasonal once by the booking's single start date and
 *   applies it to the whole stay; this preview will diverge from the real total on a range that
 *   crosses a weekend/weekday or season boundary. Chosen anyway because per-day variation reads
 *   more intuitively on the calendar). rangeStart (together with rangeEnd below) only brings in
 *   progressive duration pricing, which has no meaning without a complete range.
 * @param {Date|dayjs.Dayjs} [options.rangeEnd] - the stay's currently-picked end date. Progressive
 *   duration pricing only activates once BOTH a start and an end are picked — a start alone has
 *   no "day-of-stay" to speak of yet, so every day still shows the plain day/seasonal-only view
 *   until the range is complete. Once both are set, every date within [rangeStart, rangeEnd] is
 *   additionally stepped by getProgressiveAdjustment for its 1-based day-of-stay index, dates
 *   outside that span are unaffected. Per the backend's own stacking rule (see shared_helpers.rb's
 *   adjusted_trip_amount comment), that progressive delta is summed with the day/seasonal delta
 *   off the SAME base — not compounded on top of it.
 * @returns {{ hasPricing: boolean, priceInfoForDate: (date: dayjs.Dayjs) => { hasDiscount: boolean, headline: number, rows: Array } }}
 */
export const useDailyDiscountCalendar = (trip, seasonRanges, options = {}) => {
  const { t } = useTranslation()
  const { rangeStart, rangeEnd } = options
  const isProgressiveActive = isDynamicMultiDay(trip) && !!trip?.progressive_pricing_enabled && !!rangeStart && !!rangeEnd

  const resolvedSeasonRanges = useMemo(() => {
    if (seasonRanges) return seasonRanges
    return (trip?.available_date_ranges || [])
      .map((range) => ({ start: range.start, end: range.end }))
      .filter((range) => range.start && range.end)
  }, [seasonRanges, trip?.available_date_ranges])

  const tripPrice = useMemo(
    () => trip?.trip_prices?.find((price) => !price?._destroy) || trip?.trip_prices?.[0],
    [trip?.trip_prices]
  )
  const isFlatRate = (tripPrice?.pricing_type || trip?.pricing_type) === "charge_flat_rate"
  const dayAdjustments = useMemo(() => trip?.day_adjustments || [], [trip?.day_adjustments])
  const seasonalAdjustments = useMemo(() => trip?.seasonal_adjustments || [], [trip?.seasonal_adjustments])

  const guestRows = useMemo(() => {
    if (isFlatRate || !tripPrice) return []
    return GUEST_TYPES.filter((guest) => {
      const rate = Number(tripPrice?.[guest.rateField])
      if (!rate) return false
      return guest.allowedField ? !!tripPrice?.[guest.allowedField] : true
    })
  }, [isFlatRate, tripPrice])

  // A ticketed trip has no trip_prices: each ticket type carries its own price, and an adjustment
  // can apply to all of them or to selected ones only (QA Ticketing #38).
  const isTicketed = trip?.experience_type === EXPERIENCE_TYPES.ticketed
  const ticketTypes = useMemo(
    () => (isTicketed ? getBookableTicketTypes(trip).filter((ticketType) => Number(ticketType.price) > 0) : []),
    [isTicketed, trip]
  )

  let hasPricing = guestRows.length > 0
  if (isTicketed) hasPricing = ticketTypes.length > 0
  else if (isFlatRate) hasPricing = Number(tripPrice?.flat_rate) > 0

  const priceInfoForDate = useCallback(
    (date) => {
      const dateStr = date.format(ISO)

      // Each ticket type priced on its own, as the guest widget does; the day is discounted when any
      // of them is cheaper. The biggest saving comes first, for the calendar's "-N%".
      if (isTicketed) {
        const rates = getTicketRates({ ticketTypes, date: dateStr, dayAdjustments, seasonalAdjustments, availableDateRanges: resolvedSeasonRanges })
        const rows = ticketTypes
          .map((ticketType) => ({
            key: `ticket_${ticketType.id}`,
            label: ticketType.title,
            original: Number(ticketType.price),
            adjusted: Math.max(0, Number(rates[ticketType.id]) || 0)
          }))
          .filter((row) => row.adjusted < row.original)
          .sort((first, second) => (second.original - second.adjusted) / second.original - (first.original - first.adjusted) / first.original)
        const prices = rows.length ? rows.map((row) => row.adjusted) : ticketTypes.map((ticketType) => Number(ticketType.price))
        return { hasDiscount: rows.length > 0, headline: prices.length ? Math.min(...prices) : 0, rows }
      }

      const applicable = [
        ...getApplicableDayAdjustments(dayAdjustments, dateStr),
        ...getApplicableSeasonalAdjustments(seasonalAdjustments, dateStr, resolvedSeasonRanges)
      ]

      // Progressive delta, as a plain amount off the base — same units as the day/seasonal
      // delta above so the two can be summed rather than one compounding on the other's
      // result. getProgressiveAdjustment expresses a percent step as `multiplier` (applied
      // as base × multiplier); subtracting 1 turns that back into "just the delta portion".
      // Only meaningful once the range is COMPLETE (start and end both picked) and only for
      // dates inside it — a start alone has no "day-of-stay" to speak of yet.
      let progressiveDelta = () => 0
      if (isProgressiveActive && !date.isBefore(rangeStart, "day") && !date.isAfter(rangeEnd, "day")) {
        const { multiplier, flatDelta } = getProgressiveAdjustment(trip, countDays(rangeStart, date))
        progressiveDelta = (original) => Math.max(original * (multiplier - 1) + flatDelta, -original)
      }

      const daySeasonalDelta = (original) => applyPriceAdjustments(original, applicable) - original

      if (isFlatRate) {
        const original = Number(tripPrice?.flat_rate) || 0
        const adjusted = Math.max(0, original + daySeasonalDelta(original) + progressiveDelta(original))
        const hasDiscount = adjusted < original
        const guestCount = tripPrice?.included_guests || trip?.max_guests || 1
        return {
          hasDiscount,
          headline: hasDiscount ? adjusted : original,
          rows: hasDiscount
            ? [
                {
                  key: "flat",
                  label: t("listings.tabs.preview.pricing_preview.for_guests", {
                    count: guestCount,
                    defaultValue: `for ${guestCount} guests`
                  }),
                  original,
                  adjusted
                }
              ]
            : []
        }
      }

      const computed = guestRows.map((guest) => {
        const original = Number(tripPrice?.[guest.rateField]) || 0
        return {
          key: guest.key,
          label: t(`listings.tabs.preview.pricing_preview.${guest.key}`, { defaultValue: guest.key }),
          original,
          adjusted: Math.max(0, original + daySeasonalDelta(original) + progressiveDelta(original))
        }
      })
      // Adjustments apply uniformly (the same % or flat $ off every guest type's own base),
      // so they all move the same direction on a given day — checking one representative
      // rate is enough to decide whether the whole day counts as a discount.
      const representative = computed[0]
      const hasDiscount = representative ? representative.adjusted < representative.original : false
      return {
        hasDiscount,
        headline: hasDiscount ? representative.adjusted : (representative?.original ?? 0),
        rows: hasDiscount ? computed : []
      }
    },
    [
      dayAdjustments,
      seasonalAdjustments,
      resolvedSeasonRanges,
      isTicketed,
      ticketTypes,
      isFlatRate,
      tripPrice,
      guestRows,
      trip,
      isProgressiveActive,
      rangeStart,
      rangeEnd,
      t
    ]
  )

  return { hasPricing, priceInfoForDate, isFlatRate }
}
